import { Fragment, useCallback, type ReactElement } from "react";
import { format, startOfDay, subDays } from "date-fns";
import type { TFunction } from "i18next";
import type { SortingState } from "@tanstack/react-table";
import { FaArrowRight } from "react-icons/fa";
import { css } from "../../../../styled-system/css";
import { HStack } from "../../../../styled-system/jsx";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import type { AppColumns } from "../../../components/table/tableFeatures";
import { MetricLabel } from "../../../components/i18n/MetricLabel";
import { metricNameSelector } from "../../merchandising/utils/metrics";
import { useMarketplaceLabel } from "../../../utils/marketplaceConstants";
import { snakeCaseToSentenceCase, toTitleCase } from "../../../utils/formatText";
import AsinProductCell from "../../merchandising/components/cells/AsinCell";
import type { TSelector } from "../../../i18n/types";

export const NEW_TO_BRAND_CAMPAIGN_QUERY = "NEW_TO_BRAND_CONVERSIONS";
export const NEW_TO_BRAND_ASIN_QUERY = "NEW_TO_BRAND_CONVERSIONS_BY_ASIN";

export const normalizeQueryName = (name?: string) => {
  if (!name) {
    return name;
  }
  if (name === NEW_TO_BRAND_ASIN_QUERY) {
    return NEW_TO_BRAND_CAMPAIGN_QUERY;
  }
  return name;
};

type ParameterSchemaEntry = {
  name: string;
  type: "str" | "int" | "bool" | "list";
  kind: "sql_substitution" | "execution";
  required: boolean;
  default: unknown;
  description: string;
};

export type AudienceTemplate = {
  id: number;
  audience_template_name: string;
  audience_display_name: string;
  audience_description: string;
  audience_byline: string;
  recommended_lookalike_range: string | null;
  recommended_tags: string[];
  required_module: string | null;
  parameter_schema: ParameterSchemaEntry[];
};

export const formatAdType = (v: string) => {
  let formattedType = toTitleCase(snakeCaseToSentenceCase(v));

  if (formattedType.toLowerCase() === "dsp") {
    formattedType = formattedType.toUpperCase();
  }

  if (formattedType.endsWith("s")) {
    formattedType = formattedType.slice(0, -1);
  }

  return formattedType;
};

export const mapCountryCodeToName = (code: string) => {
  try {
    if (!code) {
      return code;
    }
    const format = new Intl.DisplayNames(["en"], { type: "region" });
    return format.of(code);
  } catch (e) {
    // oxlint-disable-next-line no-console
    console.error(e);
    return code;
  }
};

export const mapStateCodeToName = (code: string) => {
  switch (code) {
    case "US-AL":
      return "Alabama";
    case "US-AK":
      return "Alaska";
    case "US-AZ":
      return "Arizona";
    case "US-AR":
      return "Arkansas";
    case "US-CA":
      return "California";
    case "US-CO":
      return "Colorado";
    case "US-CT":
      return "Connecticut";
    case "US-DE":
      return "Delaware";
    case "US-FL":
      return "Florida";
    case "US-GA":
      return "Georgia";
    case "US-HI":
      return "Hawaii";
    case "US-ID":
      return "Idaho";
    case "US-IL":
      return "Illinois";
    case "US-IN":
      return "Indiana";
    case "US-IA":
      return "Iowa";
    case "US-KS":
      return "Kansas";
    case "US-KY":
      return "Kentucky";
    case "US-LA":
      return "Louisiana";
    case "US-ME":
      return "Maine";
    case "US-MD":
      return "Maryland";
    case "US-MA":
      return "Massachusetts";
    case "US-MI":
      return "Michigan";
    case "US-MN":
      return "Minnesota";
    case "US-MS":
      return "Mississippi";
    case "US-MO":
      return "Missouri";
    case "US-MT":
      return "Montana";
    case "US-NE":
      return "Nebraska";
    case "US-NV":
      return "Nevada";
    case "US-NH":
      return "New Hampshire";
    case "US-NJ":
      return "New Jersey";
    case "US-NM":
      return "New Mexico";
    case "US-NY":
      return "New York";
    case "US-NC":
      return "North Carolina";
    case "US-ND":
      return "North Dakota";
    case "US-OH":
      return "Ohio";
    case "US-OK":
      return "Oklahoma";
    case "US-OR":
      return "Oregon";
    case "US-PA":
      return "Pennsylvania";
    case "US-RI":
      return "Rhode Island";
    case "US-SC":
      return "South Carolina";
    case "US-SD":
      return "South Dakota";
    case "US-TN":
      return "Tennessee";
    case "US-TX":
      return "Texas";
    case "US-UT":
      return "Utah";
    case "US-VT":
      return "Vermont";
    case "US-VA":
      return "Virginia";
    case "US-WA":
      return "Washington";
    case "US-WV":
      return "West Virginia";
    case "US-WI":
      return "Wisconsin";
    case "US-WY":
      return "Wyoming";
    default:
      return code || "Unknown";
  }
};

/**
 * A stored parameter is a UTC instant; read it back as the calendar day it was submitted as.
 * Read the UTC components explicitly rather than reparsing: a bare date string parses as UTC
 * midnight, which slides back a day for anyone west of Greenwich.
 */
export const storedCalendarDay = (value?: string | null) => {
  if (!value) return null;
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return null;
  return new Date(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate());
};

/** Amazon requires an audience's ending time window to be strictly before the current date and time. */
export const latestSelectableAudienceDay = () => startOfDay(subDays(new Date(), 1));

export const toAmcTimeWindow = (value: Date) => format(startOfDay(value), "yyyy-MM-dd'T'HH:mm:ss");

export const getTemplateSpec = (templates: AudienceTemplate[], templateName: string) => {
  return templates.find(
    (audienceTemplate: AudienceTemplate) => audienceTemplate.audience_template_name === templateName,
  );
};

export const getTemplateSpecById = (templates: AudienceTemplate[], id: number | null | undefined) => {
  if (id == null) return undefined;
  return templates.find((audienceTemplate: AudienceTemplate) => audienceTemplate.id === id);
};

type ReportSpec = {
  name: string;
  display_name: TSelector;
  byline: TSelector;
  description: TSelector;
  parameter_list?: string[];
  href?: string;
  required_datasources?: string[];
  featured?: boolean;
};

export const CANNED_QUERIES: ReportSpec[] = [
  {
    name: "DSP_CAMPAIGN_PERFORMANCE_BY_LOCATION",
    display_name: ($) => $.amc.reports.dsp_campaign_performance_by_location.display_name,
    byline: ($) => $.amc.reports.dsp_campaign_performance_by_location.byline,
    description: ($) => $.amc.reports.dsp_campaign_performance_by_location.description,
    parameter_list: ["days", "subscription_title"],
  },
  {
    name: "CAMPAIGN_PERFORANCE_FIRST_TOUCH_ATTRIBUTION",
    display_name: ($) => $.amc.reports.campaign_perforance_first_touch_attribution.display_name,
    byline: ($) => $.amc.reports.campaign_perforance_first_touch_attribution.byline,
    description: ($) => $.amc.reports.campaign_perforance_first_touch_attribution.description,
    parameter_list: ["days", "subscription_title"],
  },
  {
    name: "EXPOSURE_SEGMENT_PERFORMANCE",
    display_name: ($) => $.amc.reports.exposure_segment_performance.display_name,
    byline: ($) => $.amc.reports.exposure_segment_performance.byline,
    description: ($) => $.amc.reports.exposure_segment_performance.description,
    parameter_list: ["days", "subscription_title"],
  },
  {
    name: "CAMPAIGN_STR_PLACEMENT_PERFORMANCE",
    display_name: ($) => $.amc.reports.campaign_str_placement_performance.display_name,
    byline: ($) => $.amc.reports.campaign_str_placement_performance.byline,
    description: ($) => $.amc.reports.campaign_str_placement_performance.description,
    parameter_list: ["days", "tag", "campaignIds", "productTags", "subscription_title"],
  },
  {
    name: "GATEWAY_ASINS_INTERNAL",
    display_name: ($) => $.amc.reports.gateway_asins_internal.display_name,
    byline: ($) => $.amc.reports.gateway_asins_internal.byline,
    description: ($) => $.amc.reports.gateway_asins_internal.description,
    parameter_list: ["days", "campaignIds", "asins", "tag", "productTags", "subscription_title"],
  },
  {
    name: "LTV_BY_AD_EXPOSURE_INTERNAL",
    display_name: ($) => $.amc.reports.ltv_by_ad_exposure_internal.display_name,
    byline: ($) => $.amc.reports.ltv_by_ad_exposure_internal.byline,
    description: ($) => $.amc.reports.ltv_by_ad_exposure_internal.description,
    parameter_list: ["days", "subscription_title"],
    required_datasources: ["conversions_all"],
  },
  {
    name: "SUBSCRIBE_AND_SAVE_PERFORMANCE",
    display_name: ($) => $.amc.reports.subscribe_and_save_performance.display_name,
    byline: ($) => $.amc.reports.subscribe_and_save_performance.byline,
    description: ($) => $.amc.reports.subscribe_and_save_performance.description,
    parameter_list: ["days", "asins", "subscription_title"],
    required_datasources: ["conversions_all"],
  },
  {
    name: "REACH_IMPRESSION_FREQUENCY",
    display_name: ($) => $.amc.reports.reach_impression_frequency.display_name,
    byline: ($) => $.amc.reports.reach_impression_frequency.byline,
    description: ($) => $.amc.reports.reach_impression_frequency.description,
    parameter_list: ["days", "tag", "campaignIds", "subscription_title", "productTags"],
  },
  {
    name: "ASIN_PURCHASE_OVERLAP",
    display_name: ($) => $.amc.reports.asin_purchase_overlap.display_name,
    byline: ($) => $.amc.reports.asin_purchase_overlap.byline,
    description: ($) => $.amc.reports.asin_purchase_overlap.description,
    parameter_list: ["days", "asins", "campaignIds", "tag", "productTags", "subscription_title"],
  },
  {
    name: "TIME_TO_CONVERSION",
    display_name: ($) => $.amc.reports.time_to_conversion.display_name,
    byline: ($) => $.amc.reports.time_to_conversion.byline,
    description: ($) => $.amc.reports.time_to_conversion.description,
    parameter_list: ["days", "campaignIds", "asins", "tag", "productTags", "subscription_title"],
  },
  {
    name: NEW_TO_BRAND_CAMPAIGN_QUERY,
    display_name: ($) => $.amc.reports.new_to_brand_conversions.display_name,
    byline: ($) => $.amc.reports.new_to_brand_conversions.byline,
    description: ($) => $.amc.reports.new_to_brand_conversions.description,
    parameter_list: ["days", "campaignIds", "asins", "tag", "productTags", "subscription_title"],
  },
];

export const FEATURED_REPORTS: ReportSpec[] = [
  {
    name: "SHOPPER_INSIGHTS",
    display_name: ($) => $.amc.reports.shopper_insights.display_name,
    byline: ($) => $.amc.reports.shopper_insights.byline,
    description: ($) => $.amc.reports.shopper_insights.description,
    parameter_list: ["days"],
    href: "/user/amc/shopperinsightsV2",
    featured: true,
  },
  {
    name: "PATH_TO_PURCHASE_GROUPS_INTERNAL",
    display_name: ($) => $.amc.reports.path_to_purchase_groups_internal.display_name,
    byline: ($) => $.amc.reports.path_to_purchase_groups_internal.byline,
    description: ($) => $.amc.reports.path_to_purchase_groups_internal.description,
    parameter_list: ["days"],
    href: "/user/amc/conversionchart/reports",
    featured: true,
  },
];

export const AMC_QUERY_EXPLORER_BREADCRUMB_SPEC: {
  name: TSelector;
  href: string;
  subTitle: TSelector;
} = {
  name: ($) => $.amc["AMC Dashboard"],
  href: "/user/amc/dashboard",
  subTitle: ($) => $.common.dashboardLinks["Placement"],
};

export const time_to_conversion_metrics = [
  "< 1 Min",
  "From 1min - 10min",
  "From 10min - 30min",
  "From 30min - 1h",
  "From 1h - 2h",
  "From 2h - 12h",
  "From 12h - 24h",
  "From 1 - 3 Days",
  "From 3 - 5 Days",
  "From 5 - 7 Days",
  "More than 7 Days",
] as const;

export type TimeToConversionMetrics = (typeof time_to_conversion_metrics)[number];

type AmcReportRow = Record<string, string | number | undefined>;

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type TFn = TFunction<any, any>;

type CellContent = ReactElement | string;

interface AmcReportTableSpec {
  /** The title column comes first; it identifies the row. */
  columns: (t: TFn) => AppColumns<AmcReportRow>;
  defaultSorting: SortingState;
  /** AMC queries sometimes emit blank rows; a row without this field is one and is dropped. */
  requiredField: string;
  searchFields: string[];
}

const amcColumnHelper = createColumnBuilder<AmcReportRow>();

/** CSV cells arrive as strings, so an empty one is `""` rather than missing. */
const blankToUndefined = <T,>(value: T | "" | null | undefined) => (value === "" || value == null ? undefined : value);

const amcText = (
  id: string,
  header: string | ReturnType<typeof metricNameSelector>,
  options: { size?: number; justify?: "end"; cell?: (value: string) => CellContent } = {},
) =>
  amcColumnHelper.accessor((row) => blankToUndefined(row[id])?.toString(), {
    id,
    header: typeof header === "string" ? header : () => <MetricLabel label={header} />,
    size: options.size ?? 150,
    sortFn: "alphanumeric",
    sortUndefined: "last",
    cell: ({ getValue }): CellContent => {
      const value = getValue();
      return value == null ? "-" : (options.cell?.(value) ?? value);
    },
    meta: {
      columnFilterOptions: { label: header, format: "text" },
      columnSelectorOptions: { label: header },
      justify: options.justify,
    },
  });

const amcTitle = (id: string, header: string, cell?: (value: string) => CellContent) => ({
  ...amcText(id, header, { size: 300, cell }),
  enableHiding: false,
});

const titleText = css({ fontWeight: 500 });

const asinCell = (asin: string) => <AsinProductCell asin={asin} />;

const totalRowText = css({ fontWeight: 700, textDecoration: "underline" });

const exposureCell = (exposure: string) => (
  <span className={exposure === "total" ? totalRowText : titleText}>{toTitleCase(exposure)}</span>
);

/** `exposure_group` is a bracketed list of touchpoints, e.g. `[sponsored_products,dsp]`. */
const exposureGroupCell = (exposureGroup: string) => {
  const touchpoints = exposureGroup.slice(1, -1).split(",");
  return (
    <HStack gap="small" flexWrap="wrap" className={titleText}>
      {touchpoints.map((touchpoint, index) => (
        <Fragment key={index}>
          {index > 0 && <FaArrowRight />}
          <span>{toTitleCase(snakeCaseToSentenceCase(touchpoint))}</span>
        </Fragment>
      ))}
    </HStack>
  );
};

const boldTextCell = (value: string) => <span className={titleText}>{value}</span>;

const NEW_TO_BRAND_METRICS = [
  "ntb_conversions",
  "non_ntb_conversions",
  "ntb_conversions_pct",
  "ntb_units",
  "non_ntb_units",
  "ntb_units_pct",
  "ntb_product_sales",
  "non_ntb_product_sales",
  "ntb_product_sales_pct",
];

/** Keyed by report type with any `_INTERNAL` suffix removed; see `amcReportTableSpec`. */
const TABLE_SPECS_BY_REPORT_TYPE: Record<string, AmcReportTableSpec> = {
  GATEWAY_ASINS: {
    columns: (t) => [
      amcTitle(
        "tracked_asin",
        t(($) => $.amc["Tracked ASIN"]),
        asinCell,
      ),
      ...["gateway_asin_rank", "all_users_with_purchase", "ntb_users", "ntb_percentage", "distinct_user_count"].map(
        (id) => amcColumnHelper.metric(id),
      ),
    ],
    defaultSorting: [{ id: "gateway_asin_rank", desc: false }],
    requiredField: "tracked_asin",
    searchFields: ["tracked_asin"],
  },
  LTV_BY_AD_EXPOSURE: {
    columns: (t) => [
      amcTitle(
        "exposure",
        t(($) => $.amc["Exposure"]),
        exposureCell,
      ),
      amcColumnHelper.metric("ltv"),
      amcColumnHelper.metric("customer_cnt"),
      amcColumnHelper.metric("units_sold"),
      amcColumnHelper.metric("amc_sales"),
    ],
    // Ascending keeps the "total" row, which counts every customer, at the bottom.
    defaultSorting: [{ id: "customer_cnt", desc: false }],
    requiredField: "exposure",
    searchFields: ["exposure"],
  },
  EXPOSURE_SEGMENT_PERFORMANCE: {
    columns: (t) => [
      amcTitle(
        "exposure_group",
        t(($) => $.amc["Exposure"]),
        exposureGroupCell,
      ),
      ...[
        "users_that_purchased",
        "unique_reach",
        "total_impressions",
        "total_clicks",
        "total_spend",
        "amc_total_purchases",
        "total_product_sales",
        "purchase_rate",
      ].map((id) => amcColumnHelper.metric(id)),
    ],
    defaultSorting: [{ id: "total_impressions", desc: true }],
    requiredField: "exposure_group",
    searchFields: ["exposure_group"],
  },
  SUBSCRIBE_AND_SAVE_PERFORMANCE: {
    columns: (t) => [
      amcTitle(
        "tracked_asin",
        t(($) => $.amc["Tracked ASIN"]),
        asinCell,
      ),
      // A purchase count, or "7+" for the top bucket.
      amcText("sns_purchase_frequency", metricNameSelector("sns_purchase_frequency"), { justify: "end" }),
      amcColumnHelper.metric("total_sns_count"),
      amcColumnHelper.metric("percent_total_sns"),
    ],
    defaultSorting: [{ id: "total_sns_count", desc: true }],
    requiredField: "tracked_asin",
    searchFields: ["tracked_asin"],
  },
  CAMPAIGN_PERFORANCE_FIRST_TOUCH_ATTRIBUTION: {
    columns: (t) => [
      amcTitle(
        "campaign",
        t(($) => $.amc["Campaign"]),
        boldTextCell,
      ),
      amcText("ad_product_type", metricNameSelector("ad_product_type"), { cell: formatAdType }),
      ...[
        "amc_clicks",
        "conversions",
        "amc_impressions",
        "amc_roas",
        "total_product_sales",
        "amc_total_units_sold",
        "user_reach",
        "users_that_converted",
      ].map((id) => amcColumnHelper.metric(id)),
    ],
    defaultSorting: [{ id: "total_product_sales", desc: true }],
    // Every real row carries the constant "First Touch".
    requiredField: "attribution_model",
    searchFields: ["campaign", "ad_product_type"],
  },
  DSP_CAMPAIGN_PERFORMANCE_BY_LOCATION: {
    columns: (t) => [
      amcTitle(
        "campaign",
        t(($) => $.amc["Campaign"]),
        boldTextCell,
      ),
      amcText(
        "country",
        t(($) => $.amc["Country"]),
      ),
      amcText(
        "state",
        t(($) => $.amc["State"]),
      ),
      ...[
        "unique_reach",
        "users_that_clicked",
        "total_impressions",
        "total_spend",
        "amc_detail_page_views",
        "amc_add_to_cart",
        "purchases",
        "total_product_sales",
      ].map((id) => amcColumnHelper.metric(id)),
    ],
    defaultSorting: [{ id: "total_impressions", desc: true }],
    requiredField: "campaign",
    searchFields: ["campaign", "country", "state", "dma_code"],
  },
  CAMPAIGN_STR_PLACEMENT_PERFORMANCE: {
    columns: (t) => [
      amcTitle(
        "customer_search_term",
        t(($) => $.amc["Search Term"]),
        boldTextCell,
      ),
      amcText(
        "placement_type",
        t(($) => $.amc["Placement"]),
        { size: 200 },
      ),
      ...[
        "amc_impressions",
        "amc_clicks",
        "conversions",
        "amc_ad_spend",
        "amc_ad_sales",
        "amc_acos",
        "amc_roas",
        "amc_ctr",
        "amc_conversion_rate",
        "amc_cpc",
      ].map((id) => amcColumnHelper.metric(id)),
    ],
    defaultSorting: [{ id: "amc_impressions", desc: true }],
    requiredField: "customer_search_term",
    searchFields: ["customer_search_term", "placement_type"],
  },
  REACH_IMPRESSION_FREQUENCY: {
    columns: (t) => [
      amcTitle(
        "frequency_bucket",
        t(($) => $.amc["Impressions per User"]),
      ),
      amcColumnHelper.metric("users_in_bucket"),
      amcColumnHelper.metric("impressions_in_bucket"),
      amcColumnHelper.metric("percent_of_total_users"),
    ],
    defaultSorting: [{ id: "users_in_bucket", desc: true }],
    requiredField: "frequency_bucket",
    searchFields: ["frequency_bucket", "users_in_bucket", "impressions_in_bucket", "percent_of_total_users"],
  },
  ASIN_PURCHASE_OVERLAP: {
    columns: (t) => [
      amcTitle(
        "lead_asin",
        t(($) => $.amc["Lead ASIN"]),
        asinCell,
      ),
      amcColumnHelper.metric("lead_asin_purchases"),
      amcColumnHelper.metric("dist_lead_asin_purchased_user_count"),
      amcColumnHelper.metric("dist_lead_and_overlap_asin_purchased_user_count"),
      amcColumnHelper.metric("user_overlap"),
      amcText("overlap_asin", metricNameSelector("overlap_asin"), { size: 300, cell: asinCell }),
    ],
    defaultSorting: [{ id: "user_overlap", desc: true }],
    requiredField: "lead_asin",
    searchFields: [
      "lead_asin",
      "overlap_asin",
      "lead_asin_purchases",
      "dist_lead_asin_purchased_user_count",
      "dist_lead_and_overlap_asin_purchased_user_count",
      "user_overlap",
    ],
  },
  TIME_TO_CONVERSION: {
    columns: (t) => [
      amcTitle(
        "campaign",
        t(($) => $.amc["Campaign"]),
      ),
      ...time_to_conversion_metrics.map((id) => amcColumnHelper.metric(id)),
    ],
    defaultSorting: [{ id: "campaign", desc: false }],
    requiredField: "campaign",
    searchFields: ["campaign"],
  },
  NEW_TO_BRAND_CONVERSIONS: {
    columns: (t) => [
      amcTitle(
        "campaign",
        t(($) => $.amc["Campaign"]),
      ),
      ...NEW_TO_BRAND_METRICS.map((id) => amcColumnHelper.metric(id)),
    ],
    defaultSorting: [{ id: "ntb_conversions", desc: true }],
    requiredField: "campaign",
    searchFields: ["campaign"],
  },
  NEW_TO_BRAND_CONVERSIONS_BY_ASIN: {
    columns: (t) => [
      amcTitle(
        "tracked_asin",
        t(($) => $.amc["ASIN"]),
        asinCell,
      ),
      ...NEW_TO_BRAND_METRICS.map((id) => amcColumnHelper.metric(id)),
    ],
    defaultSorting: [{ id: "ntb_conversions", desc: true }],
    requiredField: "tracked_asin",
    searchFields: ["tracked_asin"],
  },
};

/** An `_INTERNAL` report type shares its public counterpart's table. */
export const amcReportTableType = (queryType: string | undefined) => queryType?.replace(/_INTERNAL$/, "");

export const amcReportTableSpec = (reportType: string | undefined): AmcReportTableSpec | undefined =>
  reportType ? TABLE_SPECS_BY_REPORT_TYPE[reportType] : undefined;

export const FEATURED_REPORT_TYPES_WITH_DATE_SUPPORT = ["PATH_TO_PURCHASE_GROUPS_INTERNAL"];

export const AUDIENCE_LOOKALIKE_OPTIONS = [
  { value: null, label: "Exact" },
  { value: "MOST_SIMILAR", label: "Most Similar (Europe and the Americas: 500K-1M, Asia: 200K-300K)" },
  { value: "SIMILAR", label: "Similar (Europe and the Americas: 1.5M-3M, Asia: 450K-900K)" },
  { value: "BALANCED", label: "Balanced (Europe and the Americas: 2M-5.5M, Asia: 600K-1.65M)" },
  { value: "BROAD", label: "Broad (Europe and the Americas: 2.5M-7.5M, Asia: 750K-2.25M)" },
  { value: "MOST_BROAD", label: "Most Broad (Europe and the Americas: 3M-10M, Asia: 900K-3M)" },
];

export const MARKET_PLACE_ID_TO_COUNTRY_CODE_MAP: Record<string, string> = {
  ATVPDKIKX0DER: "USA",
  A2EUQ1WTGCTBG2: "CA",
  A1F83G8C2ARO7P: "UK",
  A39IBJ37TRP1C6: "AU",
  A1PA6795UKMFR9: "DE",
  A2VIGQ35RCS4UG: "AE",
  A1AM78C64UM0Y8: "MX",
  A1805IZSGTT6HS: "NL",
  A1RKKUPIHCS9HS: "ES",
  A21TJRUUN4KGV: "IN",
  A13V1IB3VIYZZH: "FR",
  APJ6JRA9NG5V4: "IT",
  AMEN7PMS3EDWL: "BE",
  A1VC38T7YXB528: "JP",
  A19VAU5U5O7RUS: "SG",
  A2NODRKZP88ZB9: "SE",
  A1C3SOZRARQ6R3: "PL",
  A33AVAJ2PDY3EV: "TR",
  A2Q3Y263D00KWC: "BR",
  A17E79C6D8DWNP: "SA",
  A28R8C7NBKEWEA: "IE",
};

export const COUNTRY_CODE_TO_MARKET_PLACE_ID_MAP: Record<string, string> = {
  USA: "ATVPDKIKX0DER",
  US: "ATVPDKIKX0DER",
  CA: "A2EUQ1WTGCTBG2",
  UK: "A1F83G8C2ARO7P",
  GB: "A1F83G8C2ARO7P",
  AU: "A39IBJ37TRP1C6",
  DE: "A1PA6795UKMFR9",
  AE: "A2VIGQ35RCS4UG",
  MX: "A1AM78C64UM0Y8",
  NL: "A1805IZSGTT6HS",
  ES: "A1RKKUPIHCS9HS",
  IN: "A21TJRUUN4KGV",
  FR: "A13V1IB3VIYZZH",
  IT: "APJ6JRA9NG5V4",
  BE: "AMEN7PMS3EDWL",
  JP: "A1VC38T7YXB528",
  SG: "A19VAU5U5O7RUS",
  SE: "A2NODRKZP88ZB9",
  PL: "A1C3SOZRARQ6R3",
  TR: "A33AVAJ2PDY3EV",
  BR: "A2Q3Y263D00KWC",
  SA: "A17E79C6D8DWNP",
  IE: "A28R8C7NBKEWEA",
};

/** Names an Amazon marketplace ID by its country, falling back to the ID itself. */
export function useMarketplaceIdLabel() {
  const marketplaceLabel = useMarketplaceLabel();
  return useCallback(
    (marketplaceId: string) => {
      const countryCode = MARKET_PLACE_ID_TO_COUNTRY_CODE_MAP[marketplaceId];
      return countryCode ? marketplaceLabel(countryCode) : marketplaceId;
    },
    [marketplaceLabel],
  );
}

export const AMC_AUDIENCE_STATUSES = [
  {
    label: "Running",
    value: "RUNNING",
  },
  {
    label: "Failed",
    value: "FAILED",
  },
  {
    label: "Succeeded",
    value: "SUCCEEDED",
  },
  {
    label: "Pending",
    value: "PENDING",
  },
  {
    label: "Deactivated",
    value: "DEACTIVATED",
  },
  {
    label: "Initial",
    value: "INITIAL",
  },
  {
    label: "Started",
    value: "STARTED",
  },
  {
    label: "Rejected",
    value: "REJECTED",
  },
];

export const DEFAULT_LOOKBACK_PERIOD_IN_DAYS = 30;

export const BASE_PATH_TO_PURCHASE_REPORT_CONFIG = {
  parameter_list: ["days"],
  parameters: { days: 90 },
  frequency: "daily",
  subscribed: true,
  isStatic: true,
  id: "pathToPurchaseBaseFeaturedReport",
};

export const AMC_CLIENT = 1;
